import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { finalize, mergeMap, takeUntil, takeWhile, tap } from "rxjs/operators";
import { SubjectModelListItem } from "../../../_objects/subject-model";
import { UtilsService } from "../../../_services/_utils.service";
import { CommonSnackBarService } from "../../../_services/common-snack-bar.service";
import { FileSystemAccessService } from "../../../_services/file-system-access.service";
import { ReportTemplateOfflineService } from "../../../_services/offline/report-template-offline.service";
import { SubjectModelOfflineService } from "../../../_services/offline/subject-model-offline.service";
import { SpinnerService } from "../../../_services/spinner.service";
import { CommonDeleteDialogComponent } from "../../../common-dialog/common-delete-dialog.component";
import { CommonErrorMessageDialogComponent } from "../../../common-dialog/common-error-message-dialog.component";
import { CommonImportConfirmDialogComponent } from "../../../common-dialog/common-import-confirm-dialog/common-import-confirm-dialog.component";
import { ReportTemplateListItem, UpdateIdList } from "../../models/report-preview/report-template";
import { NetworkStatusService } from "../../../_services/network-status.service";
import { IndividualReportTemplateDbService } from "../../../_services/database/individual-report-template-db.service";
import { Subject } from "rxjs";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { IVisolFilePickerService, VISOL_FILE_PICKER_SERVICE } from "../../../common-dialog/file-manager/visol-file-picker.service";
import { COMPRESSED_FILETYPE } from "../../../app.constants";

@Component({
  selector: "app-offline-report-template",
  templateUrl: "./offline-report-template.component.html",
  styleUrls: ["./offline-report-template.component.scss"],
  standalone: false
})
export class OfflineReportTemplateComponent implements OnInit, OnDestroy {
  private onDestroy$: Subject<object> = new Subject<object>();
  isOnPremiseMode: boolean = false;

  public displayedColumns: Array<string> = ["name", "date", "subjectModelName", "actions"];
  /** レポートテンプレート一覧用データソース。 */
  public reportTemplateListsDataSource: ReportTemplateListItem[] = [];
  /** 検査テンプレート一覧。 */
  public subjectModelListItems: Array<SubjectModelListItem> = new Array<SubjectModelListItem>();
  public pdfUrl: SafeResourceUrl | null = null;

  /** レポートテンプレートが存在するかどうか。 */
  public get isReportTemplateExists(): boolean {
    return this.reportTemplateListsDataSource.length > 0;
  }

  constructor(
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private reportTemplateOfflineService: ReportTemplateOfflineService,
    private snackBar: CommonSnackBarService,
    private subjectModelOfflineService: SubjectModelOfflineService,
    private translate: TranslateService,
    private fileAccessService: FileSystemAccessService,
    private networkStatusService: NetworkStatusService,
    private utilsService: UtilsService,
    private individualReportTemplateDbService: IndividualReportTemplateDbService,
    private sanitizer: DomSanitizer,
    @Inject(VISOL_FILE_PICKER_SERVICE) private visolFilePicker: IVisolFilePickerService
  ) {}

  public ngOnInit(): void {
    this.setReportTemplateLists();
    this.isOnPremiseMode = this.networkStatusService.isOnPremise;
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  public get useApplicationFileManager() {
    return this.networkStatusService.useApplicationFileManager();
  }

  private setReportTemplateLists(): void {
    this.oscSpinnerService.show();
    this.subjectModelOfflineService
      .getSubjectModels(false, false)
      .pipe(
        mergeMap((x) => {
          this.subjectModelListItems = x;
          return this.reportTemplateOfflineService.getReportTemplates(null, false);
        }),
        finalize(() => this.oscSpinnerService.hide())
      )
      .subscribe(
        (value) => {
          this.reportTemplateListsDataSource = value;
        },
        (error) => {
          console.log(error);
        }
      );
  }

  private openErrorMessageDialog(firstMessage: string, secondMessage: string): void {
    this.dialog.open(CommonErrorMessageDialogComponent, {
      width: "450px",
      height: "400px",
      data: { firstMessage: firstMessage, secondMessage: secondMessage }
    });
  }

  public changeTargetSubjectModels(event: any, rowData: ReportTemplateListItem): void {
    if (!event.value || !Array.isArray(event.value)) {
      return;
    }

    const updateIdList: UpdateIdList = new UpdateIdList();
    updateIdList.subjectModelIds = event.value as Array<string>;

    this.oscSpinnerService.show();
    this.reportTemplateOfflineService
      .updateModelsForTemplates(rowData.id, updateIdList)
      .pipe(
        mergeMap(() => this.reportTemplateOfflineService.getReportTemplateListById(rowData.id)),
        finalize(() => this.oscSpinnerService.hide())
      )
      .subscribe(
        (value) => {
          this.reportTemplateListsDataSource.forEach((x) => {
            if (x.id === value.id) {
              // 更新結果を反映。
              Object.assign(x, value);
            }
          });
        },
        (error) => {
          console.log(error);
          this.snackBar.open(this.translate.instant("offlineReportTemplate.saveFailed"));
        }
      );
  }

  public importReportTemplate(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const selectedFiles = (event.target as HTMLInputElement).files;

    if (selectedFiles.length === 1) {
      const selectedReportTemplateFile: File = selectedFiles[0];
      this.processSelectedReportTemplate(selectedReportTemplateFile);
    }
  }

  processSelectedReportTemplate(reportTemplateFile: File) {
    this.dialog
      .open(CommonImportConfirmDialogComponent, {
        width: CommonImportConfirmDialogComponent.defaultDialogWidth,
        height: CommonImportConfirmDialogComponent.defaultDialogHeight,
        data: {
          fileName: reportTemplateFile.name
        }
      })
      .afterClosed()
      .pipe(
        takeWhile((x) => !!x),
        tap(() => this.oscSpinnerService.show()),
        mergeMap(() => this.reportTemplateOfflineService.importReportTemplate(reportTemplateFile)),
        finalize(() => this.oscSpinnerService.hide())
      )
      .subscribe(
        (value) => {
          this.snackBar.open(this.translate.instant("offlineReportTemplate.importReportTemplateSuccessfully"));
          this.setReportTemplateLists();
        },
        (error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.BadRequest) {
            this.openErrorMessageDialog(
              this.translate.instant("offlineReportTemplate.importReportTemplateFailed"),
              this.translate.instant("offlineReportTemplate.importReportTemplateFailedMessage")
            );
          } else {
            this.snackBar.open(this.translate.instant("offlineReportTemplate.importReportTemplateFailed"));
            console.log(error);
          }
        }
      );
  }

  public async viewPdfPreview(reportTemplate: ReportTemplateListItem) {
    const file = await this.fileAccessService.getFile(reportTemplate.pdfPreviewPath, `${reportTemplate.name}.pdf`, "application/pdf");

    if (file && file.type === "application/pdf") {
      const reader = new FileReader();
      reader.onload = () => {
        const pdfUrlBase = reader.result as string;
        const pdfUrlWithConfig = this.networkStatusService.isBrianMode ? `${pdfUrlBase}#toolbar=0` : pdfUrlBase;
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrlWithConfig);
      };
      reader.readAsDataURL(file);
    }
  }

  public closePdf() {
    this.pdfUrl = null;
  }

  deleteOnPremiseReportTemplate(reportTemplate: ReportTemplateListItem) {
    this.individualReportTemplateDbService
      .hasReports(reportTemplate.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((hasReports) => {
        if (hasReports) {
          this.openErrorMessageDialog(this.translate.instant("offlineReportTemplate.deleteReportTemplateWithReportsFailedMessage"), "");
        } else {
          this.deleteReportTemplate(reportTemplate);
        }
      });
  }

  public deleteReportTemplate(reportTemplate: ReportTemplateListItem): void {
    this.dialog
      .open(CommonDeleteDialogComponent, {
        width: CommonDeleteDialogComponent.defaultDialogWidth,
        minWidth: CommonDeleteDialogComponent.defaultDialogMinWidth,
        maxHeight: CommonDeleteDialogComponent.defaultDiaMaxHeight,
        data: {
          target: this.translate.instant("offlineReportTemplate.reportTemplate"),
          deleteNames: [reportTemplate.name]
        }
      })
      .afterClosed()
      .pipe(
        takeWhile((x) => !!x),
        tap(() => this.oscSpinnerService.show()),
        mergeMap(() => this.reportTemplateOfflineService.deleteReportTemplate(reportTemplate.id)),
        finalize(() => this.oscSpinnerService.hide())
      )
      .subscribe({
        next: () => {
          this.snackBar.open(this.translate.instant("offlineReportTemplate.deleteReportTemplateSuccessfully"));
          this.setReportTemplateLists();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.Conflict) {
            this.openErrorMessageDialog(
              this.translate.instant("offlineReportTemplate.deleteReportTemplateFailed"),
              this.translate.instant("offlineReportTemplate.deleteReportTemplateFailedMessage")
            );
          } else {
            this.snackBar.open(this.translate.instant("offlineReportTemplate.deleteReportTemplateFailed"));
            console.log(error);
          }
        }
      });
  }

  public exportTemplate(reportTemplate: ReportTemplateListItem) {
    this.reportTemplateOfflineService
      .exportReportTemplate(reportTemplate.id)
      .subscribe((zip) => this.utilsService.download(zip, `${reportTemplate.name}.zip`, null));
  }

  showFileManager() {
    this.visolFilePicker.importFiles({ extensions: [COMPRESSED_FILETYPE.EXTENSION_ZIP], multiple: false }).then((files) => {
      if (files.length === 1) {
        this.processSelectedReportTemplate(files[0]);
      }
    });
  }
}
